import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/pt-br";
import { saveAs } from "file-saver";
import { pdf } from "@react-pdf/renderer";
import { useEffect, useState, useMemo, useRef } from "react";

import api from "../../services/api";

import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { renderToStaticMarkup } from "react-dom/server";
import PDFLineChartComponent from "../pdfSlaCharts";
import PDFPieChart from "../pdfSlaCharts/pieChart";
import { useTheme } from "@mui/system";
import { PageTemplateEasyReport } from "../generateReport";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.locale("pt-br");

export default function RelatorioAtaquePdfEmail() {
  // Obter dados da URL
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dadosString = searchParams.get("dados");
  const dadosDecodificados = JSON.parse(decodeURIComponent(dadosString));
  // States
  const [blobString, setBlobString] = useState(null);
  const [loading, setLoading] = useState(false);
  const [triggered, setTriggered] = useState(false);
  // Estilos
  const theme = useTheme();

  useEffect(() => {
    if (!triggered) {
      handleDados();
      setTriggered(true);
    }
  }, []);

  // Gerar relatório
  const handleDados = async () => {
    try {
      setLoading(true);
      // Obter configurações de cabeçalho
      const config = await api.post("/find/generalSetting", {
        product: "usageReport",
      });
      const logo = config?.data?.imagem
        ? await handleUrlToBase64({ url: config?.data?.imagem })
        : null;
      const generalSettings =
        Object.keys(config?.data)?.length > 0
          ? {
              logo: logo,
              ...config?.data,
            }
          : {};
      // Obter json do relatório gerado
      const report = await api.post("/find/historyReport", {
        id: dadosDecodificados?.id,
      });
      // Gerar gráficos
      const graphLatency = await handleGenerateGraphPNG({
        chart: (
          <WrapAreaChart data={report?.data?.graphData?.latencyData} type={2} />
        ),
        width: 900,
        height: 150,
      });
      const graphTrafficIn = await handleGenerateGraphPNG({
        chart: (
          <WrapAreaChart
            data={report?.data?.graphData?.trafficInData}
            refLine={
              report?.data?.selectPages?.selectConsume?.percentile
                ? [
                    {
                      name: "Th",
                      value: report?.data?.relatorio?.percentil,
                      opt: { label: { fill: "red" } },
                    },
                  ]
                : []
            }
          />
        ),
        width: 900,
        height: 150,
      });
      const graphPacketLoss = await handleGenerateGraphPNG({
        chart: (
          <WrapAreaChart
            data={report?.data?.graphData?.packetLossData}
            type={1}
          />
        ),
        width: 900,
        height: 150,
      });
      const graphAvailability = await handleGenerateGraphPNG({
        chart: (
          <WrapPieChart data={report?.data?.graphData?.availabilityData} />
        ),
        width: 400,
        height: 260,
      });

      // Montar objeto com gráficos
      const graphcs = {
        type: "noPacket",
        graphLatency: "",
        graphPacketLoss: "",
        graphPing: "",
        graphcs: "",
      };
      graphcs.graphLatency = graphLatency;
      graphcs.graphcs = graphTrafficIn;
      graphcs.graphPacketLoss = graphPacketLoss;
      graphcs.graphPing = graphAvailability;

      // Criar arquivo PDF
      const MyDoc = (
        <PageTemplateEasyReport
          relatorio={{
            ...report?.data?.relatorio,
            source: `Monitoramento ${
              window?.config?.REACT_APP_NAME_ENTERPRISE ||
              process.env.REACT_APP_NAME_ENTERPRISE
            }`,
          }}
          graphcs={{ ...graphcs }}
          enterpriseConfig={{ ...generalSettings }}
          selectPages={{
            ...report?.data?.selectPages,
            teoric: report?.data?.theoreticalFramework,
          }}
        />
      );

      // Gerar o PDF como Blob e converter para base64
      const blob = await pdf(MyDoc).toBlob();
      const blobAsBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result.split(",")[1];
          resolve(base64data);
        };
        reader.readAsDataURL(blob);
      });

      setBlobString(blobAsBase64);

      // Download do arquivo (apenas para testar com navegador)
      // saveAs(blob, `relatório ${report?.data?.relatorio?.titulo}.pdf`);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Gerar Imagems
  async function handleGenerateGraphPNG({ chart, width = 790, height = 200 }) {
    let staticHtmlTraffic = renderToStaticMarkup(chart);
    let graphc = await handleApiGetBase64({
      data: btoa(unescape(encodeURIComponent(staticHtmlTraffic))),
      width: width,
      height: height,
    });
    return graphc;
  }
  async function handleApiGetBase64(htmlBase64) {
    try {
      const response = await api.post("/cliente/relatorio/htmlToImageBase64", {
        htmlBase64,
      });

      if (response.data) {
        return response.data;
      }
    } catch (error) {
      throw error;
    }
  }
  async function handleUrlToBase64({ url, options = {} }) {
    try {
      const response = await api.post("/cliente/relatorio/urlToImageBase64", {
        url,
        options,
      });

      if (response.data) {
        return response?.data;
      }
    } catch (error) {
      console.error("Error: ", error);
      return null;
    }
  }

  // Renderizar Gráficos
  const WrapAreaChart = ({ data, type = 0, refLine = [] }) => {
    return (
      <PDFLineChartComponent
        mainColor={theme.palette.cardGraphic.graphColor.green}
        mainLineColor={theme.palette.cardGraphic.graphLineColor.green}
        data={data || []}
        theme={theme}
        areaChartOptions={{ height: 140, width: 900 }}
        type={type}
        referenceLines={refLine}
      />
    );
  };
  const WrapPieChart = ({ data }) => {
    return (
      <PDFPieChart
        data={
          !!data
            ? [
                { name: "Disponível", value: data?.mean * 100 },
                {
                  name: "Indisponível",
                  value: 100 - data?.mean * 100,
                },
              ]
            : []
        }
        theme={theme}
      />
    );
  };

  return (
    <>
      <Box id="pdf">{blobString && blobString}</Box>
    </>
  );
}
